import React from "react";
import { Box, Text } from "@mantine/core";
import { GetTransactionsResponse, Payment } from "../../../types/transactions";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import { formatDateTime } from "../../../utils/timeUtils";
import { formatPaymentMethod } from "../../../utils/paymentMethodFormatters";
import { formatPhoneForDisplay } from "../../../utils/studioSettings";
import {
  computeTransactionTotals,
  paymentRefundable,
} from "../../../utils/transactionTotals";
import DetailPanel from "./DetailPanel";
import {
  SectionLabel,
  ClientBlock,
  LineItemRow,
  PaymentCard,
} from "./panelParts";
import { transactionStatusKey } from "./StatusCell";
import {
  appliedToLabel,
  canVoidTransaction,
  clientName,
  detailLineItems,
  netTotal,
  refundedTotal,
} from "./salesDerivations";
import styles from "./sales.module.css";

/* ── Transaction detail slide-over body ──────────────────────────────────── */

interface TransactionDetailProps {
  overlay: boolean;
  row: GetTransactionsResponse;
  /** IANA timezone of the business location; all datetimes render in it. */
  timezone: string;
  onClose: () => void;
  onRefund: (payment: Payment) => void;
  onPrint: () => void;
  onEmail: () => void;
  emailDisabled: boolean;
  emailLoading: boolean;
  /** Opens the void confirmation. Shown only for unpaid transactions. */
  onVoid?: () => void;
  /** Opens the underlying appointment (undefined for non-appointment sales). */
  onOpenAppointment?: () => void;
}

function TransactionDetailDrawer({
  overlay,
  row,
  timezone,
  onClose,
  onRefund,
  onPrint,
  onEmail,
  emailDisabled,
  emailLoading,
  onVoid,
  onOpenAppointment,
}: TransactionDetailProps) {
  const txn = row.transaction;
  const totals = computeTransactionTotals(row);
  const lines = detailLineItems(row);
  const refunds = refundedTotal(row);
  const net = netTotal(row);
  // All real tenders incl. loyalty redemption (shown as a tender, per the
  // appointment-summary parity); refunds excluded (they have their own rows).
  const tenders = (row.payments || []).filter(
    (p) => p.status === "succeeded" && p.type !== "refund",
  );
  // A voided transaction is out of every total: no balance to collect and no
  // refunds to issue from here (display only; nothing else is locked).
  const isVoid = txn?.transaction_status === "void";
  const balanceDue = isVoid ? 0 : totals.remainingBalance;
  const statusKey = transactionStatusKey(
    txn?.transaction_status,
    txn?.refund_status,
  );

  return (
    <DetailPanel
      overlay={overlay}
      title={`Transaction #${txn?.reference_id || "—"}`}
      meta={
        txn?.created_dt
          ? formatDateTime(txn.created_dt, timezone, "MMM D, YYYY · h:mm A")
          : ""
      }
      statusKey={statusKey}
      onClose={onClose}
      onPrint={onPrint}
      onEmail={onEmail}
      emailDisabled={emailDisabled}
      emailLoading={emailLoading}
      onVoid={canVoidTransaction(row) ? onVoid : undefined}
    >
      <Box>
        <ClientBlock
          name={clientName(row)}
          phone={
            formatPhoneForDisplay(
              row.client?.area_code,
              row.client?.phone_number,
            ) || undefined
          }
          email={row.client?.email || undefined}
          txnRef={row.transaction?.reference_id || undefined}
          onTxnClick={onOpenAppointment}
        />
      </Box>

      <Box>
        <SectionLabel count={lines.length}>Line items</SectionLabel>
        {lines.length === 0 ? (
          <Text className={styles.cellMuted} size="sm">
            No line items.
          </Text>
        ) : (
          lines.map((line, i) => (
            <LineItemRow
              key={line.key}
              name={line.name}
              staff={line.staff}
              amount={line.amount}
              qty={line.qty}
              unitPrice={line.unitPrice}
              first={i === 0}
            />
          ))
        )}

        <Box className={styles.totalsBlock}>
          <Box className={styles.kvRow}>
            <Text component="span" className={styles.cellMuted}>
              Subtotal
            </Text>
            <Text component="span" className={styles.cellNum}>
              {formatCurrency(totals.subtotalAfterRedemptions)}
            </Text>
          </Box>
          {totals.discountSavings > 0 && (
            <Box className={styles.kvRow}>
              <Text component="span" className={styles.cellMuted}>
                Discount
              </Text>
              <Text component="span" className={styles.cellNum}>
                −{formatCurrency(totals.discountSavings)}
              </Text>
            </Box>
          )}
          {totals.salesTax > 0 && (
            <Box className={styles.kvRow}>
              <Text component="span" className={styles.cellMuted}>
                Tax
              </Text>
              <Text component="span" className={styles.cellNum}>
                {formatCurrency(totals.salesTax)}
              </Text>
            </Box>
          )}
          {totals.tipsTotal > 0 && (
            <Box className={styles.kvRow}>
              <Text component="span" className={styles.cellMuted}>
                Tip
              </Text>
              <Text component="span" className={styles.cellNum}>
                {formatCurrency(totals.tipsTotal)}
              </Text>
            </Box>
          )}
          <Box className={styles.kvRow}>
            <Text component="span" className={styles.netTotalLabel}>
              Total
            </Text>
            <Text component="span" className={styles.netTotalValue}>
              {formatCurrency(totals.totalAfterRedemptions)}
            </Text>
          </Box>
          {refunds > 0 && (
            <>
              <Box className={styles.kvRow}>
                <Text component="span" className={styles.totalsRefundLabel}>
                  Refunded
                </Text>
                <Text component="span" className={styles.totalsRefundValue}>
                  −{formatCurrency(refunds)}
                </Text>
              </Box>
              <Box className={styles.kvRow}>
                <Text component="span" className={styles.netTotalLabel}>
                  Net
                </Text>
                <Text component="span" className={styles.netTotalValue}>
                  {formatCurrency(net)}
                </Text>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {(txn?.loyalty_points_earned ?? 0) > 0 && (
        <Box>
          <SectionLabel>Loyalty</SectionLabel>
          <Box className={styles.kvRow}>
            <Text component="span" className={styles.cellMuted}>
              Points earned
            </Text>
            <Text component="span" className={styles.cellNum}>
              {txn?.loyalty_points_earned}
            </Text>
          </Box>
        </Box>
      )}

      <Box>
        <SectionLabel count={tenders.length}>Payments</SectionLabel>
        {tenders.length === 0 ? (
          <Text className={styles.cellMuted} size="sm">
            No payment taken.
          </Text>
        ) : (
          tenders.map((payment) => (
            <PaymentCard
              key={payment.id}
              method={formatPaymentMethod(payment.method) || payment.method}
              brand={payment.card_brand}
              last4={payment.card_last4}
              iconMethod={payment.method}
              meta={`${appliedToLabel(payment)} · ${formatDateTime(
                payment.created_dt,
                timezone,
                "h:mm A",
              )}`}
              amount={payment.amount}
              refundedAmount={payment.refunded_amount}
              onRefund={isVoid ? undefined : () => onRefund(payment)}
              refundDisabled={paymentRefundable(payment) <= 0}
            />
          ))
        )}

        {balanceDue > 0 && (
          <Box className={styles.balanceDue}>
            <Text component="span">Balance due</Text>
            <Text component="span" className={styles.balanceDueValue}>
              {formatCurrency(balanceDue)}
            </Text>
          </Box>
        )}
      </Box>
    </DetailPanel>
  );
}

export default TransactionDetailDrawer;
